import { Box, Button, Flex, Select, Stack, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { getProviderAbsences } from '../../../services/absences/providerAbsenceService';
import { getPracticesList } from '../../../services/practices/getPractices';
import { getProviderById } from '../../../services/providers/getProviderById';
import {
  ProviderAbsence,
  ProviderAbsenceGetParams,
} from '../../../types/absences/types';
import { Provider } from '../../../types/practices/types';

interface MarketAbsencesListProps {
  marketId: string[];
}

// Extended type with practice name and provider object
type ProviderAbsenceWithName = ProviderAbsence & {
  practiceName: string;
  provider?: Provider;
};

// Helper function to fetch all practices for a market
const fetchAllPracticesForMarket = async (
  marketId: string
): Promise<Record<string, string>> => {
  const practicesMap: Record<string, string> = {};
  let startToken: string | null = null;
  let hasMore = true;

  while (hasMore) {
    const response = await getPracticesList(
      startToken,
      { marketIds: marketId },
      100
    );
    if (response.data?.practices) {
      response.data.practices.forEach((practice) => {
        practicesMap[practice.id] = practice.name;
      });
      // Check if there are more pages
      startToken = response.data.nextToken || null;
      hasMore = !!startToken;
    } else {
      hasMore = false;
    }
  }

  return practicesMap;
};

// Define columns for the table view with custom rendering for clickable names
const getAbsenceColumns = (navigate: (path: string) => void) => [
  {
    header: 'Practice Name',
    accessor: 'practiceName',
    width: '20%',
    isCustomRender: true,
    render: (row: ProviderAbsenceWithName) => (
      <Text
        fontSize="sm"
        color="blue.500"
        _hover={{
          cursor: 'pointer',
          textDecoration: 'underline',
          color: 'blue.700',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (row.practiceId) {
            navigate(`/practices/${row.practiceId}`);
          }
        }}
      >
        {row.practiceName || 'Unknown Practice'}
      </Text>
    ),
  },
  {
    header: 'Provider Name',
    accessor: 'provider',
    width: '20%',
    isCustomRender: true,
    render: (row: ProviderAbsenceWithName) => {
      if (!row.provider) {
        return (
          <Text fontSize="sm" color="gray.500">
            Unknown Provider
          </Text>
        );
      }
      return (
        <Box onClick={(e) => e.stopPropagation()}>
          <ProviderName
            provider={row.provider}
            mode="table"
            size="sm"
            showTooltip={true}
            highlightDuplicates={true}
          />
        </Box>
      );
    },
  },
  { header: 'Start Date', accessor: 'startDate', width: '12%' },
  { header: 'End Date', accessor: 'endDate', width: '12%' },
  { header: 'Start Time', accessor: 'startTime', width: '12%' },
  { header: 'End Time', accessor: 'endTime', width: '12%' },
  { header: 'Notes', accessor: 'notes', width: '12%' },
];

const MarketAbsencesList = ({
  marketId,
}: MarketAbsencesListProps) => {
  const [sortOrder, setSortOrder] = useState<
    'ascending' | 'descending'
  >('ascending');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDataFetched, setIsDataFetched] = useState(false);

  const [absences, setAbsences] = useState<ProviderAbsenceWithName[]>(
    []
  );
  const toast = useToast();

  const buttonBg = useColorModeValue(
    'background.secondary',
    'gray.700'
  );
  const buttonHoverBg = useColorModeValue('gray.300', 'gray.600');
  const selectBorderColor = 'background.secondary';

  // Memoized sorted absences
  const sortedAbsences = useMemo(() => {
    return [...absences].sort((a, b) => {
      const nameA = a.practiceName?.toUpperCase() ?? '';
      const nameB = b.practiceName?.toUpperCase() ?? '';
      return sortOrder === 'ascending'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [absences, sortOrder]);

  const handleAbsenceClick = useCallback(
    (absence: ProviderAbsenceWithName) => {
      // Navigate to the provider detail page
      navigate(`/providers/${absence.providerId}`);
    },
    [navigate]
  );

  const fetchAbsences = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ProviderAbsenceGetParams = {
        marketId: marketId[0], // Use the first market ID
      };

      // Fetch absences first
      const absencesResponse = await getProviderAbsences(params);

      // Fetch all practices for the market (not just 10)
      const practicesMap = await fetchAllPracticesForMarket(
        marketId[0]
      );

      // Extract unique provider IDs from absences
      const uniqueProviderIds = [
        ...new Set(
          absencesResponse.absences.map((a) => a.providerId)
        ),
      ];

      // Fetch provider data for all unique provider IDs
      const providerPromises = uniqueProviderIds.map((providerId) =>
        getProviderById(providerId).catch(() => null)
      );
      const providerResponses = await Promise.all(providerPromises);

      // Create a map of provider IDs to provider objects
      const providersMap: Record<string, Provider> = {};
      providerResponses.forEach((response, index) => {
        if (response?.data?.provider) {
          providersMap[uniqueProviderIds[index]] =
            response.data.provider;
        }
      });

      // Merge practice names and provider objects with absences data
      const absencesWithNames = absencesResponse.absences.map(
        (absence) => {
          // Get practice ID from practiceIds array (new format) or practiceId (old format)
          const practiceId = absence.practiceIds && absence.practiceIds.length > 0
            ? absence.practiceIds[0]
            : absence.practiceId;

          return {
            ...absence,
            practiceName:
              practiceId ? (practicesMap[practiceId] || 'Unknown Practice') : 'Unknown Practice',
            provider: providersMap[absence.providerId],
          };
        }
      );
      setAbsences(absencesWithNames);
      setIsDataFetched(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch absences';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [marketId, toast]);

  useEffect(() => {
    if (marketId && marketId.length > 0) {
      fetchAbsences();
    }
  }, [fetchAbsences, marketId]);

  const handleRetry = useCallback(() => {
    fetchAbsences();
  }, [fetchAbsences]);

  if (loading && !isDataFetched) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <NoItemsFound
        mainMessage="Failed to load absences"
        detailMessage={error}
        refetch={handleRetry}
      />
    );
  }

  if (!absences || absences.length === 0) {
    return (
      <Box ml="2rem" mr="2rem" mb="3rem" mt="1.75rem">
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="300px"
          textAlign="center"
        >
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            color="text.secondary"
            mb="0.5rem"
          >
            No Absences Found
          </Text>
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1.5rem"
            maxW="400px"
          >
            Use "Manage Practices" to add practices - provider
            absences will show here once practices are added.
          </Text>
          <Button
            colorScheme="blue"
            size="md"
            onClick={() =>
              navigate(`/markets/${marketId[0]}/practices`)
            }
            _hover={{ transform: 'translateY(-1px)' }}
            transition="all 0.2s ease-in-out"
          >
            Manage Practices
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      padding="1.5rem"
      borderRadius="0.5rem"
      border="1px solid"
      borderColor="gray.200"
      width="100%"
      mb="3rem"
      mt="1.75rem"
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb="1rem"
      >
        <Text
          fontSize="1.25rem"
          fontWeight="700"
          color="text.secondary"
        >
          Market Absences
        </Text>
        <Flex>
          <Select
            bg={buttonBg}
            width="10.375rem"
            mr="0.5rem"
            height="2.5rem"
            _hover={{ bg: buttonHoverBg }}
            _active={{
              bg: buttonHoverBg,
              transform: 'scale(0.98)',
            }}
            borderColor={selectBorderColor}
            _focus={{ boxShadow: 'outline' }}
            borderRadius={'0.375rem'}
            border="none"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(
                e.target.value as 'ascending' | 'descending'
              )
            }
          >
            <option value="ascending">Practice A - Z</option>
            <option value="descending">Practice Z - A</option>
          </Select>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        </Flex>
      </Flex>

      <Stack spacing="1rem">
        {viewMode === 'list' ? (
          <ResourceGrid
            data={sortedAbsences}
            columns={getAbsenceColumns(navigate)}
          />
        ) : (
          <Box overflowX="auto" width="100%">
            <table
              style={{ width: '100%', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  {getAbsenceColumns(navigate).map((column) => (
                    <th
                      key={column.accessor}
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#f7fafc',
                        fontWeight: '600',
                        width: column.width || 'auto',
                      }}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedAbsences.map((absence) => (
                  <tr
                    key={absence.id}
                    onClick={() => handleAbsenceClick(absence)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        '#f7fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        'transparent';
                    }}
                  >
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '20%',
                      }}
                    >
                      <Text
                        fontSize="sm"
                        color="blue.500"
                        _hover={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: 'blue.700',
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          const practiceId = absence.practiceIds && absence.practiceIds.length > 0
                            ? absence.practiceIds[0]
                            : absence.practiceId;
                          if (practiceId) {
                            navigate(`/practices/${practiceId}`);
                          }
                        }}
                      >
                        {absence.practiceName || 'Unknown Practice'}
                      </Text>
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '20%',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {absence.provider ? (
                        <ProviderName
                          provider={absence.provider}
                          mode="table"
                          size="sm"
                          showTooltip={true}
                          highlightDuplicates={true}
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          Unknown Provider
                        </Text>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '12%',
                      }}
                    >
                      {absence.startDate || '-'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '12%',
                      }}
                    >
                      {absence.endDate || '-'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '12%',
                      }}
                    >
                      {absence.startTime || '-'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '12%',
                      }}
                    >
                      {absence.endTime || '-'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e2e8f0',
                        width: '12%',
                      }}
                    >
                      {absence.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default MarketAbsencesList;
